import { AxiosResponse } from 'axios';
import { Installments } from 'src/components/OrderSummary/types';
import { Environment } from 'src/utils/Environment';

export const INSTALLMENT_LENGTHS = {
  [Installments.P]: 1,
  [Installments.IP3]: 3,
};

interface InstallmentPlan {
  code: Installments;
  name: string;
}

interface ProductPriceResellerOverride {
  reseller: Reseller;
  price: Money;
}

interface SoftwarePlanPricing {
  monthlyPrice: Money;
  additionalDevicePrice: Money;
  transactionRates: SoftwarePlanTransactionRates;
}

interface SoftwarePlanTransactionRates {
  keyedInRate: TransactionRate;
  inPersonRate: TransactionRate;
  resellerOverrides: TransactionRateResellerOverride[];
}

interface TransactionRateResellerOverride {
  reseller: Reseller;
  transactionRates: SoftwarePlanTransactionRates;
}

export interface TransactionRate {
  percentage: number;
  fixedAmount: Money;
}

interface Reseller {
  uuid: string;
  cosUuid: string;
  name: string;
  isMsrpEnabled: boolean;
  isDemoAvailable: boolean;
  supportedInstallmentPlans: InstallmentPlan[];
}

export interface Money {
  value: number;
  currencyCode: string;
}

export interface ProductPricing {
  installmentPlan: InstallmentPlan;
  price: Money;
  priceOverrides: ProductPriceResellerOverride[];
}

export interface Product {
  id: string;
  name: string;
  fdmpId: string;
  msrp: Money;
  isIncludedInMultiDevicePricing: boolean;
  isSelfServicePurchaseEnabled: boolean;
  pricing: ProductPricing[];
  supportedSoftwarePlans?: SoftwarePlan[];
  accessories?: Product[];
}

export interface RecommendedAddonProduct {
  product: Product;
}

export interface IncludedProduct {
  product: Product;
  quantity: number;
}

export interface SoftwarePlan {
  id: string;
  cosUuid: string;
  name: string;
  pricing: SoftwarePlanPricing | null;
}

export interface Bundle {
  id: string;
  name: string;
  softwarePlan: SoftwarePlan;
  isSelfServicePurchaseEnabled: boolean;
  includedProducts: IncludedProduct[];
  recommendedAddons: RecommendedAddonProduct[];
  productPricing: ProductPricing[];
  softwarePlanPricing: SoftwarePlanPricing;
  msrp: Money | null;
}

export type InstallmentPlanPricing = Partial<Record<Exclude<Installments, Installments.P>, Money>>;

export const minimumValidInstallmentPlanCost = 200;

export const shouldDisplayInstallmentPlans = (orderSubtotal: number) => {
  return !!orderSubtotal && orderSubtotal > minimumValidInstallmentPlanCost;
};

export const fetchBundlesDetail = async ({
  resellerId,
  bundleIds,
}: {
  resellerId: string;
  bundleIds: string[];
}): Promise<AxiosResponse<Bundle[]>> => {
  return Environment.current.dcsApiClient.get<Bundle[]>(`v1/resellers/${resellerId}/bundles`, {
    params: {
      bundleIds: bundleIds.join(','),
    },
  });
};

export const fetchProductsDetail = async ({
  resellerId,
  productIds,
}: {
  resellerId: string;
  productIds: string[];
}): Promise<AxiosResponse<Product[]>> => {
  return Environment.current.dcsApiClient.get<Product[]>(`v1/resellers/${resellerId}/products`, {
    params: {
      productIds: productIds.join(','),
    },
  });
};

export const fetchAllProducts = async (): Promise<AxiosResponse<Product[]>> => {
  return Environment.current.dcsApiClient.get<Product[]>(`v1/products`);
};
